/**
 * UserDirectory - the people who post chores, for moderators only
 * ----------------------------------------------------------------
 * The mirror of StudentDirectory, and it exists for one reason: somebody has
 * to be able to look at what adults are showing to children. A photograph, a
 * name and a town are all a sixteen-year-old sees before deciding whether to
 * go to that person's house.
 *
 * Until this existed a moderator could only reach a poster through a report
 * about them, which meant a student had to notice first - and the whole point
 * of section 7 of HANDOFF is that when the operator is also the only
 * moderator, spotting something yourself is at least as likely.
 *
 * NOT LINKED FROM ANY ORDINARY SCREEN, and the server refuses it to anybody
 * without the badge - `requireModerator` on GET /api/users, exactly like the
 * student list. A browsable list of people is not something an ordinary use
 * of this site needs, and the rule lives on the route rather than on whether
 * a link is drawn.
 *
 * Suspended people are shown too, behind a toggle. Hiding them would mean a
 * moderator who suspends somebody from here has no way to undo it: no report
 * points at them, and they have vanished from the only screen that could put
 * them back. Same lesson as the Taken down tab.
 */

import { useEffect, useState, useCallback } from 'react';
import { Search, Users } from 'lucide-react';

import { userApi } from '../services/api';
import ProfileCard from '../components/ProfileCard';
import ModeratePerson from '../components/ModeratePerson';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import BackLink from '../components/BackLink';
import { useI18n } from '../hooks/useI18n';

export default function UserDirectory() {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [showSuspended, setShowSuspended] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userApi.browse({
        search,
        include: showSuspended ? 'suspended' : '',
        limit: 24,
      });
      setUsers(data.users);
      setTotal(data.total);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, showSuspended]);

  useEffect(() => {
    // Waiting a moment stops a request per keystroke while somebody types a
    // name - the same pattern as the student directory.
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <div className="container section">
      <BackLink to="/moderation" label={t('directory.backToModeration')} />

      <div className="page-header">
        <h1>{t('directory.usersTitle')}</h1>
        <p>{t('directory.usersSubtitle')}</p>
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <div className="field">
          <label htmlFor="user-search">
            <Search size={14} style={{ display: 'inline', verticalAlign: -2 }} />{' '}
            {t('directory.searchPeople')}
          </label>
          <input
            id="user-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('directory.usersSearchPlaceholder')}
          />
        </div>

        <label className="row" style={{ gap: 10, cursor: 'pointer', marginTop: 'var(--space-xs)' }}>
          <input
            type="checkbox"
            checked={showSuspended}
            onChange={(event) => setShowSuspended(event.target.checked)}
          />
          <span>{t('directory.includeSuspended')}</span>
        </label>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <Loader label={t('directory.loadingUsers')} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('directory.noUsers')}
          message={t('directory.noUsersBody')}
        />
      ) : (
        <>
          <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
            {t('directory.usersFound', { count: total })}
          </p>

          <div className="grid-2">
            {users.map((person) => (
              <ProfileCard key={person._id} person={person}>
                {/* The email, because two people called Rossi is the ordinary
                    case and a moderator has to be sure which one they are
                    about to shut out. Only ever sent to a moderator. */}
                <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                  {person.email}
                </p>
                <ModeratePerson person={person} onChanged={load} />
              </ProfileCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
